import { useState, useEffect } from "react";
import "./population.scss";
import { FormattedMessage } from "react-intl";
import LabelLang, { LabelLangString } from "component/LabelLang/label-lang";
import { SvgLoader, SvgProxy } from "react-svgmt";
// import { DateFormater } from "functions/dateTimeFormat";
// import { useSelector } from "react-redux";
import useSWR from "swr";
import { END_POINTS } from "constants/APIConstant";
// import fetch from "auth/FetchInterceptor";
import { API_POPULATION_BASE_URL, API_LIVE_POPULATION_BASE_URL } from "configs/AppConfig";
import axios from "axios";
import useCountryPopulationService from "services/CountryPopulationService";
import DefaultPopulationComponent from "./components/default-population-component";
import { numberFormater } from "functions/displayingNumbersFormat";
import PopulationByDateComponent from "./components/population-by-date-component";
import ContentLoading from "component/LoadingComponent/ContentLoading";
import {
  POPULATION_DATE_CONSTANT,
  POPULATION_DATE_OPTIONS,
} from "constants/PopulationTimeZoneConstant";
import { useSelector } from "react-redux";

function Population() {
  const currentLocale = useSelector(({ theme }) => theme.locale);

  const [isCurrentPopulation, setIsCurrentPopulation] = useState(true);
  //To validate if there is a selected date in the population
  const [isDateExists, setIsDateExistis] = useState(false);
  // Live governorates population API
  const getLiveGovernoratesPopulation = useSWR(
    `${API_LIVE_POPULATION_BASE_URL}/population-proxy/GetPopulationData`,
    (url) => {
      return axios.get(url).then((r) => {
        // Transform the response to match the expected format
        // API returns Code, Name, Number - need to convert to id, name, population
        return {
          data: r.data.map(gov => ({
            id: parseInt(gov.Code),
            name: gov.Name,
            population: gov.Number,
            governorateTranslations: [{ name: gov.Name }]
          }))
        };
      });
    },
    { 
      refreshInterval: 30000,
      onError: (error) => {
        console.log("Live governorates population API failed, falling back to cloud function API");
      }
    }
  );

  const governoratesFetching = useSWR({ url: END_POINTS.GOVERNORATES });
  const [countryPopulationDetails, changeCountryId] =
    useCountryPopulationService({ 
      isCurrentPopulation: isCurrentPopulation,
      shouldFetchEgpop: false // Don't fetch egpop from CountryPopulationService, we'll use it as fallback in getCloudFunctionPopulation
    });
  const defaultDate = new Date(new Date().setDate(new Date().getDate() - 1))
    .toJSON()
    .slice(0, 10);
  const [countryPopulationData, setCountryPopulationData] = useState({
    date: defaultDate,
    governorateId: 1,
  });
  useEffect(() => {
    if (isCurrentPopulation || !isDateExists) {
      setCountryPopulationData({ ...countryPopulationData, date: defaultDate, governorateId: countryPopulationData.governorateId || 1 });
    }
  }, [isCurrentPopulation, isDateExists]);

  // Live Egypt total population API
  const getLiveEgyptPopulation = useSWR(
    `${API_LIVE_POPULATION_BASE_URL}/population-proxy/total`,
    (url) => {
      return axios.get(url).then((r) => {
        return { population: r.data };
      });
    },
    { 
      refreshInterval: 30000,
      onError: (error) => {
        console.log("Live Egypt population API failed, falling back to original API");
      }
    }
  );

  // First fallback to cloud function API (only when live API fails)
  const getCloudFunctionPopulation = useSWR(
    getLiveEgyptPopulation.error ? `${API_POPULATION_BASE_URL}/${END_POINTS.CURRENT_EGYPT_POPULATION}?city=0` : null,
    (url) => {
      return axios.get(url).then((r) => {
        return r.data;
      });
    },
    { 
      refreshInterval: 30000,
      onError: (error) => {
        console.log("Cloud function population API failed, falling back to backend API");
      }
    }
  );

  // Final fallback to original backend API (only when both live and cloud function APIs fail)
  const getPopulationData = useSWR(
    (getLiveEgyptPopulation.error && getCloudFunctionPopulation.error) ? 
    { url: END_POINTS.EGYPT_POPULATION_BY_DATE, params: { date: defaultDate } } : null,
    null,
    { refreshInterval: 30000 }
  );
  const getEgyptPopulationByDateData = useSWR(
    //!isCurrentPopulation && isDateExists
    {
      url: END_POINTS.EGYPT_POPULATION_BY_DATE,
      params: {
        date: isDateExists ? countryPopulationData.date : defaultDate,
      },
    }
  );
  const getEgyptPopulationByDate = () => {
    if (
      getEgyptPopulationByDateData.data &&
      !getEgyptPopulationByDateData.error &&
      !getEgyptPopulationByDateData.isLoading
    ) {
      if (getEgyptPopulationByDateData.data.data === null)
        return (
          <h4>
            <FormattedMessage id="data-not-found-in-date" />
          </h4>
        );
      const totalPopulationInString =
        getEgyptPopulationByDateData.data.data.population.toString();
      const formatedTotalPopulation = numberFormater({
        numberInString: totalPopulationInString,
      });
      return formatedTotalPopulation;
    }
    return 0;
  };
  const onPopulationDateOptionsChange = (e) => {
    const { value } = e.target;
    if (value === POPULATION_DATE_CONSTANT.NOW) {
      setIsCurrentPopulation(true);
    } else {
      setIsCurrentPopulation(false);
    }
  };

  const sortGovernorates = (a, b) => {
    if (currentLocale == "ar") {
      const nameA = a.name.toUpperCase(); // ignore upper and lowercase
      const nameB = b.name.toUpperCase(); // ignore upper and lowercase
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }

      // names must be equal
      return 0;
    } else {
      const nameA = a.governorateTranslations[0].name.toUpperCase(); // ignore upper and lowercase
      const nameB = b.governorateTranslations[0].name.toUpperCase(); // ignore upper and lowercase
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }

      // names must be equal
      return 0;
    }
  };

  const getPopulationDataByCountry = useSWR(
    countryPopulationData.date !== ""
      ? {
          url: END_POINTS.API_POPULATION_BY_COUNTRY,
          params: countryPopulationData,
        }
      : null
  );

  const getPopulationMinMaxDatesByCountry = useSWR({
    url: END_POINTS.API_MIN_MAX_BY_COUNTRY,
    params: {
      governorateId: countryPopulationData?.governorateId,
    },
  });

  const onCountryChange = (e) => {
    const { name, value } = e.target;
    if (isCurrentPopulation) {
      changeCountryId({ id: value });
    }
    setCountryPopulationData({ ...countryPopulationData, [name]: value });
  };
  const getTotalPopulationNumber = () => {
    // Show loading if the primary API is still loading
    if (getLiveEgyptPopulation.isLoading) {
      return "loading";
    }
    
    // Priority 1: Try live Egypt population API first
    if (
      getLiveEgyptPopulation.data &&
      !getLiveEgyptPopulation.isLoading &&
      !getLiveEgyptPopulation.error
    ) {
      const totalPopulationInString =
        getLiveEgyptPopulation.data.population.toString();
      const formatedTotalPopulation = numberFormater({
        numberInString: totalPopulationInString,
      });
      return formatedTotalPopulation;
    }
    
    // Show loading if fallback API is loading
    if (getCloudFunctionPopulation.isLoading) {
      return "loading";
    }
    
    // Priority 2: First fallback to cloud function API
    if (
      getCloudFunctionPopulation.data &&
      !getCloudFunctionPopulation.isLoading &&
      !getCloudFunctionPopulation.error
    ) {
      const totalPopulationInString =
        getCloudFunctionPopulation.data.population.toString();
      const formatedTotalPopulation = numberFormater({
        numberInString: totalPopulationInString,
      });
      return formatedTotalPopulation;
    }
    
    // Show loading if final fallback API is loading
    if (getPopulationData.isLoading) {
      return "loading";
    }
    
    // Priority 3: Final fallback to backend API
    if (
      getPopulationData.data &&
      !getPopulationData.isLoading &&
      !getPopulationData.error
    ) {
      const totalPopulationInString =
        getPopulationData.data.data?.population?.toString() || "0";
      const formatedTotalPopulation = numberFormater({
        numberInString: totalPopulationInString,
      });
      return formatedTotalPopulation;
    }
    return 0;
  };
  const [hasSetDefaultGovernorate, setHasSetDefaultGovernorate] = useState(false);
  
  useEffect(() => {
    // Only set Cairo as default once when governorates data first loads
    if (!hasSetDefaultGovernorate && !governoratesFetching.isLoading && !governoratesFetching.error && governoratesFetching.data) {
      const countryDetailsData = governoratesFetching.data.data.find(
        (att) => att.id === 1 // Cairo
      );
      if (countryDetailsData) {
        setCountryPopulationData(prev => ({
          ...prev,
          governorateId: countryDetailsData.id,
        }));
        setHasSetDefaultGovernorate(true);
      }
    }
  }, [governoratesFetching.data, hasSetDefaultGovernorate]);
  
  // Create a mapping between governorate names and their population from live API
  // useEffect(() => {
  //   if (!getLiveGovernoratesPopulation.isLoading && !getLiveGovernoratesPopulation.error && getLiveGovernoratesPopulation.data) {
  //     console.log("Live governorates data loaded:", getLiveGovernoratesPopulation.data);
  //   }
  // }, [getLiveGovernoratesPopulation.data]);
  const getCountryList = () => {
    // Always use governorates lookup from END_POINTS.GOVERNORATES
    // The live population API is only for population numbers, not for governorate list
    if (!governoratesFetching.isLoading && !governoratesFetching.error && governoratesFetching.data) {
      return governoratesFetching.data.data
        .sort(sortGovernorates)
        .map(({ name, id, governorateTranslations }, index) => {
          return (
            <option
              key={index + 1}
              value={id}
              title={LabelLangString({
                id: "name",
                currentLocale: currentLocale,
                defaultValue: name,
                translations: governorateTranslations,
              })}
            >
              &nbsp;&nbsp;
              <LabelLang
                id="name"
                defaultValue={name}
                translations={governorateTranslations}
              />
              &nbsp;&nbsp;
            </option>
          );
        });
    }
    return [];
  };
  const getCountryPopulationDetails = () => {
    if (
      getPopulationDataByCountry.error ||
      getPopulationDataByCountry.isLoading ||
      !getPopulationDataByCountry.data
    ) {
      return "-";
    }
    return getPopulationDataByCountry.data.data;
  };

  const getCountryMinMaxPopulationDates = () => {
    if (
      getPopulationMinMaxDatesByCountry.error ||
      getPopulationMinMaxDatesByCountry.isLoading ||
      !getPopulationMinMaxDatesByCountry.data
    ) {
      return null;
    }
    return getPopulationMinMaxDatesByCountry.data.data;
  };

  const PopulationDetails = ({ img, titleId, subTitleId, value }) => {
    return (
      <li>
        <div className="row align-items-center">
          <div className="col-2">
            <img
              loading="lazy"
              className="img-fluid"
              src={process.env.PUBLIC_URL + img}
              alt="img-title"
            />
          </div>
          <div className="col-10" style={{ alignSelf: "end" }}>
            <div className="population__info-item">
              <div className="col-5 col-sm-6">
                <p className="h5 mb-0 align-self-center title-item">
                  <FormattedMessage id={titleId} />
                </p>
              </div>

              {!value || value === "null" ? (
                <span className="mb-0 align-self-center data-not-found-in-date">
                  <FormattedMessage id="data-not-found-in-date" />
                </span>
              ) : (
                <div className="col-7 col-sm-6 d-flex items-center">
                  <p className="h4 mb-0 align-self-end value-item">
                    <center className="align-self-end">{value}</center>
                  </p>
                  &nbsp;&nbsp;&nbsp;
                  <span className="mb-0 align-self-end sub-title-item">
                    (<FormattedMessage id={subTitleId} />)
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </li>
    );
  };
  const onDateFilterChange = (passedValue) => {
    const value = passedValue === "" ? null : passedValue.target.value;
    value ? setIsDateExistis(true) : setIsDateExistis(false);
    setCountryPopulationData({
      ...countryPopulationData,
      date: value === null ? "" : value,
    });
  };
  const renderPopulation = () => {
    // console.log("Country Population Details: ", countryPopulationDetails);

    if (isCurrentPopulation) {
      // Show loading if the primary governorates API is still loading
      if (getLiveGovernoratesPopulation.isLoading) {
        return "loading";
      }
      
      // Priority 1: Try to get population from live governorates data first
      if (!getLiveGovernoratesPopulation.isLoading && !getLiveGovernoratesPopulation.error && getLiveGovernoratesPopulation.data) {
        // Note: The live API returns data.data structure
        const data = getLiveGovernoratesPopulation.data.data || getLiveGovernoratesPopulation.data;
        
        // Get the selected governorate from the lookup API  
        const governorateFromLookup = governoratesFetching.data?.data?.find(
          gov => gov.id == countryPopulationData.governorateId
        );
        
        // console.log("Selected governorate ID:", countryPopulationData.governorateId);
        // console.log("Governorate found in lookup:", governorateFromLookup);
        // console.log("Governorate code from lookup:", governorateFromLookup?.code);
        
        // Match by code - the live API uses Code field and backend will return code in lookup
        const selectedGovernorate = data.find(
          gov => gov.id == governorateFromLookup?.code
        );
        
        // console.log("Found selected governorate in live API:", selectedGovernorate);
        
        if (selectedGovernorate && selectedGovernorate.population) {
          return numberFormater({
            numberInString: selectedGovernorate.population.toString(),
          });
        }
      }
      
      // Priority 2: First fallback - for individual governorates, we use the original service
      // since cloud function only provides Egypt total
      // Priority 3: Final fallback to original service
      if (countryPopulationDetails && countryPopulationDetails !== "No Data") {
        return numberFormater({
          numberInString: countryPopulationDetails,
        });
      }
      
      // If all else fails and no data is found, return empty
      return null;
    } else {
      // if (countryPopulationDetails === "No Data") {
      //   return numberFormater({
      //     numberInString: getCountryPopulationDetails().population.toString(),
      //   });
      // }

      if (getCountryPopulationDetails() === null) return null;
      return getCountryPopulationDetails() != "-"
        ? numberFormater({
            numberInString: getCountryPopulationDetails().population.toString(),
          })
        : "-";
    }
  };

  return (
    <div className="population">
      <div className="container">
        <div className="m__title">
          <h3>
            {isCurrentPopulation ? (
              <FormattedMessage id="now-population-clock" />
            ) : (
              <FormattedMessage id="current-population-clock" />
            )}
          </h3>
        </div>
        <div className="row pb-3">
          <div className="col-12 col-lg-6">
            <div className="population__count">
              <div className="population__count-item">
                <span className="fz-20">
                  <FormattedMessage id="total-population" />
                </span>
                <h1 style={{ marginBottom: "0px" }}>
                  {isCurrentPopulation ? (
                    getTotalPopulationNumber() === "loading" ? (
                      <ContentLoading 
                        isLoading={true} 
                        height="60px" 
                        iconWidth="50px" 
                        marginTop="10px"
                      />
                    ) : (
                      getTotalPopulationNumber() || getEgyptPopulationByDate()
                    )
                  ) : (
                    getEgyptPopulationByDate()
                  )}
                </h1>
              </div>
              <div className="population__count-img">
                <SvgLoader path={"EgyptMap.svg"}>
                  {/* Important! this proxy will reset the color to black,
                    otherwise old elements would still be shown in red
                    because this library doesn't store previous states */}
                  <SvgProxy selector={"path"} fill="#FFF" />

                  <SvgProxy
                    key={countryPopulationData?.governorateId}
                    selector={`#c-${countryPopulationData?.governorateId},#c-${countryPopulationData?.governorateId} path`}
                    fill="var(--main-color)"
                  />
                </SvgLoader>
              </div>
            </div>
          </div>
          <div className="col-12 col-lg-6">
            <div className="row pb-3">
              <div className="population__info-form">
                <div className="row " style={{ width: "1000px" }}>
                  <div className="col-12 col-lg-2 align-self-center">
                    <label className="fz-20">
                      <FormattedMessage id="time" />
                    </label>
                  </div>
                  <div className="col-12 col-lg-10">
                    <div className="dropdown">
                      <select
                        name="population-date-options"
                        onChange={onPopulationDateOptionsChange}
                      >
                        {POPULATION_DATE_OPTIONS.map(({ name, id }) => (
                          <option key={id} value={id}>
                            &nbsp;&nbsp;{name}&nbsp;&nbsp;
                          </option>
                        ))}
                      </select>
                      <div className="dropdown-icon" />
                    </div>
                  </div>
                </div>
              </div>
              {isCurrentPopulation ? (
                <DefaultPopulationComponent />
              ) : (
                <PopulationByDateComponent
                  dateValue={countryPopulationData.date}
                  onDateChange={onDateFilterChange}
                  minDate={getCountryMinMaxPopulationDates()?.minDate}
                  maxDate={getCountryMinMaxPopulationDates()?.maxDate}
                />
              )}
              <div className="population__info-form">
                <div className="row pb-3" style={{ width: "1000px" }}>
                  <div className="col-12 col-lg-2 align-self-center">
                    <label className="fz-20">
                      <FormattedMessage id="choose-city" />
                    </label>
                  </div>
                  <div className="col-12 col-lg-10">
                    <div className="dropdown">
                      <select
                        name="governorateId"
                        value={countryPopulationData.governorateId ?? 0}
                        onChange={onCountryChange}
                      >
                        {getCountryList()}
                      </select>
                      <div className="dropdown-icon" />
                    </div>
                  </div>
                </div>
              </div>
              <ul>
                <PopulationDetails
                  img={"/image/Location.svg"}
                  titleId="location"
                  subTitleId="km2"
                  value={
                    getCountryPopulationDetails() != "-"
                      ? getCountryPopulationDetails() === null
                        ? null
                        : parseFloat(
                            getCountryPopulationDetails()?.governorate?.area
                          ).toFixed(2)
                      : "-"
                  }
                />
                <br />
                <PopulationDetails
                  img={"/image/Population.svg"}
                  titleId="population"
                  subTitleId="citizen"
                  value={
                    renderPopulation() === "loading" ? (
                      <ContentLoading 
                        isLoading={true} 
                        height="30px" 
                        iconWidth="30px" 
                        marginTop="5px"
                      />
                    ) : (
                    getCountryPopulationDetails() !== null && renderPopulation()
                    )
                  }
                />
                <br />
                <PopulationDetails
                  img={"/image/PopulationDensity.svg"}
                  titleId="populationDensity"
                  subTitleId="citizenKm2"
                  value={
                    getCountryPopulationDetails() != "-"
                      ? getCountryPopulationDetails() === null
                        ? null
                        : (
                            getCountryPopulationDetails().population /
                            parseFloat(
                              getCountryPopulationDetails()?.governorate?.area
                            )
                          ).toFixed(2)
                      : "-"
                  }
                />
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Population;


